import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatPrice } from '../../../utils/formatters';
import { productService, FullProductDetails, ProductVariant, ProductReview, SimpleProduct } from '../../../services/productService';
import { cartService } from '../../../services/cartService';
import { useAuth } from '../../../contexts/AuthContext';
import { eventService } from '../../../services/eventService';
import { openExternalUrl } from '../../../services/webviewService';

interface ProductDetailModalProps {
  productId: number;
  isOpen: boolean;
  onClose: () => void;
  agent_chat_id?: number;
  previousProductId?: number | null;
  nextProductId?: number | null;
  onNavigateToProduct?: (productId: number) => void;
}

const REVIEWS_PER_PAGE = 5;
const PRODUCT_MODAL_HISTORY_KEY = 'aiAgentProductModal';

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  productId,
  isOpen,
  onClose,
  agent_chat_id,
  previousProductId,
  nextProductId,
  onNavigateToProduct,
}) => {
  const { userInfo } = useAuth();
  const [product, setProduct] = useState<FullProductDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState<string | null>(null);
  const [showAllBadges, setShowAllBadges] = useState(false);

  // Reviews pagination state
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [reviewsTotalCount, setReviewsTotalCount] = useState(0);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);

  // Similar & vendor products
  const [similarProducts, setSimilarProducts] = useState<SimpleProduct[]>([]);
  const [vendorProducts, setVendorProducts] = useState<SimpleProduct[]>([]);
  const [loadingSimilarProducts, setLoadingSimilarProducts] = useState(false);
  const [loadingVendorProducts, setLoadingVendorProducts] = useState(false);
  const [hasMoreSimilarProducts, setHasMoreSimilarProducts] = useState(false);
  const [hasMoreVendorProducts, setHasMoreVendorProducts] = useState(false);

  // Keep reference to onClose to avoid stale closures
  const onCloseRef = useRef(onClose);
  // Track if modal is currently open to prevent multiple popstate handlers
  const isModalOpenRef = useRef(false);
  // Refs for infinite scroll observers
  const vendorProductsContainerRef = useRef<HTMLDivElement>(null);
  const similarProductsContainerRef = useRef<HTMLDivElement>(null);
  // Refs to track latest values for scroll handlers
  const hasMoreVendorProductsRef = useRef(hasMoreVendorProducts);
  const hasMoreSimilarProductsRef = useRef(hasMoreSimilarProducts);
  const loadingVendorProductsRef = useRef(loadingVendorProducts);
  const loadingSimilarProductsRef = useRef(loadingSimilarProducts);

  // Get display photos with featured photo first
  const displayPhotos = product
    ? product.photo && !product.photos.includes(product.photo)
      ? [product.photo, ...product.photos]
      : product.photos
    : [];

  useEffect(() => {
    if (isOpen && productId) {
      // Reset state
      setLoading(true);
      setReviews([]);
      setReviewsTotalCount(0);
      setHasMoreReviews(false);
      setSelectedPhotoIndex(0);
      setSimilarProducts([]);
      setVendorProducts([]);
      setHasMoreSimilarProducts(false);
      setHasMoreVendorProducts(false);
      setShowAllBadges(false);

      // Load product details, then fetch reviews from dedicated reviews API
      productService.getFullProductDetails(productId).then(async (data) => {
        setProduct(data);
        setLoading(false);
        if (data?.variants && data.variants.length > 0) {
          setSelectedVariantId(data.variants[0].id);
        }

        // Always load reviews from /api/products/:id/reviews endpoint
        try {
          const reviewsResponse = await productService.getProductReviews(productId, REVIEWS_PER_PAGE, 0);
          if (reviewsResponse && reviewsResponse.reviews.length > 0) {
            setReviews(reviewsResponse.reviews);
            setReviewsTotalCount(reviewsResponse.total_count);
            setHasMoreReviews(reviewsResponse.has_next);
          } else {
            setReviews([]);
            setReviewsTotalCount(0);
            setHasMoreReviews(false);
          }
        } catch (error) {
          console.error('Error loading initial reviews:', error);
          setReviews([]);
          setReviewsTotalCount(0);
          setHasMoreReviews(false);
        }

        // Capture product modal view event
        if (agent_chat_id) {
          void eventService.captureProductClick(agent_chat_id, productId, 'productModal');
        }
      });

      // Load similar products & vendor products in parallel (15 each)
      productService.getSimilarProducts(productId, 15).then((products) => {
        setSimilarProducts(products);
        setHasMoreSimilarProducts(products.length === 15);
      });
      productService.getVendorProducts(productId, 15).then((products) => {
        setVendorProducts(products);
        setHasMoreVendorProducts(products.length === 15);
      });
    }
  }, [isOpen, productId, agent_chat_id]);

  const loadMoreReviews = async () => {
    setLoadingReviews(true);
    try {
      const data = await productService.getProductReviews(productId, REVIEWS_PER_PAGE, reviews.length);
      if (data && data.reviews.length > 0) {
        setReviews((prev) => [...prev, ...data.reviews]);
        setReviewsTotalCount(data.total_count);
        setHasMoreReviews(data.has_next);
      } else {
        setHasMoreReviews(false);
      }
    } catch (error) {
      console.error('Error loading more reviews:', error);
      setHasMoreReviews(false);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleLoadMoreReviews = () => {
    loadMoreReviews();
  };

  // Update refs when state changes
  useEffect(() => {
    hasMoreVendorProductsRef.current = hasMoreVendorProducts;
  }, [hasMoreVendorProducts]);

  useEffect(() => {
    hasMoreSimilarProductsRef.current = hasMoreSimilarProducts;
  }, [hasMoreSimilarProducts]);

  useEffect(() => {
    loadingVendorProductsRef.current = loadingVendorProducts;
  }, [loadingVendorProducts]);

  useEffect(() => {
    loadingSimilarProductsRef.current = loadingSimilarProducts;
  }, [loadingSimilarProducts]);

  const loadMoreSimilarProducts = useCallback(async () => {
    if (loadingSimilarProductsRef.current) return;
    // Update ref immediately to prevent duplicate calls (state update is async)
    loadingSimilarProductsRef.current = true;
    setLoadingSimilarProducts(true);
    try {
      const currentLength = similarProducts.length;
      const products = await productService.getSimilarProducts(productId, 15, currentLength);
      if (products.length > 0) {
        setSimilarProducts((prev) => [...prev, ...products]);
        setHasMoreSimilarProducts(products.length === 15);
      } else {
        setHasMoreSimilarProducts(false);
      }
    } catch (error) {
      console.error('Error loading more similar products:', error);
      setHasMoreSimilarProducts(false);
    } finally {
      loadingSimilarProductsRef.current = false;
      setLoadingSimilarProducts(false);
    }
  }, [productId, similarProducts.length]);

  const loadMoreVendorProducts = useCallback(async () => {
    if (loadingVendorProductsRef.current) return;
    // Update ref immediately to prevent duplicate calls (state update is async)
    loadingVendorProductsRef.current = true;
    setLoadingVendorProducts(true);
    try {
      const currentLength = vendorProducts.length;
      const products = await productService.getVendorProducts(productId, 15, currentLength);
      if (products.length > 0) {
        setVendorProducts((prev) => [...prev, ...products]);
        setHasMoreVendorProducts(products.length === 15);
      } else {
        setHasMoreVendorProducts(false);
      }
    } catch (error) {
      console.error('Error loading more vendor products:', error);
      setHasMoreVendorProducts(false);
    } finally {
      loadingVendorProductsRef.current = false;
      setLoadingVendorProducts(false);
    }
  }, [productId, vendorProducts.length]);

  // Refs to hold latest load functions (avoids recreating scroll listeners)
  const loadMoreVendorProductsFnRef = useRef(loadMoreVendorProducts);
  const loadMoreSimilarProductsFnRef = useRef(loadMoreSimilarProducts);
  useEffect(() => { loadMoreVendorProductsFnRef.current = loadMoreVendorProducts; }, [loadMoreVendorProducts]);
  useEffect(() => { loadMoreSimilarProductsFnRef.current = loadMoreSimilarProducts; }, [loadMoreSimilarProducts]);

  // Helper: check if an RTL horizontal scroll container is near the end
  const isNearScrollEnd = (container: HTMLDivElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = container;
    if (scrollWidth === 0) return false;
    // In RTL, scrollLeft is 0 at start and goes negative. Math.abs handles both LTR & RTL.
    const scrolledAmount = Math.abs(scrollLeft);
    const scrollPercentage = (scrolledAmount + clientWidth) / scrollWidth;
    return scrollPercentage >= 0.8 || scrollWidth <= clientWidth;
  };

  // Scroll event listener for infinite scroll - Vendor Products
  // NOTE: `loading` is in deps so the effect re-runs when containers actually mount
  // (products may load before product details, so containers aren't in DOM yet)
  useEffect(() => {
    const container = vendorProductsContainerRef.current;
    if (!container || vendorProducts.length === 0) return;

    let ticking = false;

    const checkAndLoad = () => {
      if (!hasMoreVendorProductsRef.current || loadingVendorProductsRef.current) return;
      if (isNearScrollEnd(container)) {
        loadMoreVendorProductsFnRef.current();
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkAndLoad();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Check initially after DOM is ready
    const timeoutId = setTimeout(checkAndLoad, 300);

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [vendorProducts.length, loading]);

  // Re-check vendor products scroll after new products are appended
  useEffect(() => {
    if (vendorProducts.length === 0 || loadingVendorProducts || loading) return;
    
    const container = vendorProductsContainerRef.current;
    if (!container) return;

    const timeoutId = setTimeout(() => {
      if (!hasMoreVendorProductsRef.current || loadingVendorProductsRef.current) return;
      if (isNearScrollEnd(container)) {
        loadMoreVendorProductsFnRef.current();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [vendorProducts.length, loadingVendorProducts, loading]);

  // Scroll event listener for infinite scroll - Similar Products
  // NOTE: `loading` is in deps so the effect re-runs when containers actually mount
  useEffect(() => {
    const container = similarProductsContainerRef.current;
    if (!container || similarProducts.length === 0) return;

    let ticking = false;

    const checkAndLoad = () => {
      if (!hasMoreSimilarProductsRef.current || loadingSimilarProductsRef.current) return;
      if (isNearScrollEnd(container)) {
        loadMoreSimilarProductsFnRef.current();
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkAndLoad();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Check initially after DOM is ready
    const timeoutId = setTimeout(checkAndLoad, 300);

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [similarProducts.length, loading]);

  // Re-check similar products scroll after new products are appended
  useEffect(() => {
    if (similarProducts.length === 0 || loadingSimilarProducts || loading) return;
    
    const container = similarProductsContainerRef.current;
    if (!container) return;

    const timeoutId = setTimeout(() => {
      if (!hasMoreSimilarProductsRef.current || loadingSimilarProductsRef.current) return;
      if (isNearScrollEnd(container)) {
        loadMoreSimilarProductsFnRef.current();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [similarProducts.length, loadingSimilarProducts, loading]);

  // Update onClose ref when it changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (!isModalOpenRef.current) return;
    if (window.history.state?.[PRODUCT_MODAL_HISTORY_KEY]) {
      // Let the popstate handler close the modal and consume its history entry.
      window.history.back();
      return;
    }
    isModalOpenRef.current = false;
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Mark modal as open
      isModalOpenRef.current = true;

      // Prevent body scroll
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // Add history entry to handle back button
      window.history.pushState(
        { ...window.history.state, [PRODUCT_MODAL_HISTORY_KEY]: true },
        '',
      );

      // Handle browser back button (popstate event)
      const handlePopState = () => {
        if (isModalOpenRef.current) {
          isModalOpenRef.current = false;
          onCloseRef.current();
        }
      };
      window.addEventListener('popstate', handlePopState);

      // Handle Android back button (webview)
      const originalBackFromAndroid = window.backFromAndroid;
      const handleAndroidBack = () => {
        requestClose();
      };
      if (typeof window.backFromAndroid === 'function') {
        window.backFromAndroid = handleAndroidBack;
      }

      return () => {
        isModalOpenRef.current = false;
        window.removeEventListener('popstate', handlePopState);
        document.body.style.overflow = originalOverflow;

        if (originalBackFromAndroid) {
          window.backFromAndroid = originalBackFromAndroid;
        }
      };
    }
  }, [isOpen, requestClose]);

  if (!isOpen) return null;

  const handleAddToCart = async () => {
    if (!userInfo?.is_authenticated) {
      setAddToCartMessage('لطفاً اول وارد حساب کاربریت شو');
      setTimeout(() => setAddToCartMessage(null), 3000);
      return;
    }

    if (product?.variants && product.variants.length > 0 && !selectedVariantId) {
      setAddToCartMessage('لطفاً یه تنوع انتخاب کن');
      setTimeout(() => setAddToCartMessage(null), 3000);
      return;
    }

    setAddingToCart(true);
    setAddToCartMessage(null);

    try {
      const result = await cartService.addToCart({
        product_id: productId,
        quantity: 1,
        variation_id: selectedVariantId || undefined,
      });

      if (result.success) {
        setAddToCartMessage('✓ به سبد خریدت اضافه شد');
        setTimeout(() => setAddToCartMessage(null), 3000);

        if (agent_chat_id) {
          void eventService.captureProductClick(agent_chat_id, productId, 'addToCart');
        }
      } else {
        setAddToCartMessage(result.message || 'متأسفانه یه مشکلی پیش اومد، به سبد خریدت اضافه نشد.');
        setTimeout(() => setAddToCartMessage(null), 3000);
      }
    } catch (error) {
      setAddToCartMessage('متأسفانه یه مشکلی پیش اومد، به سبد خریدت اضافه نشد.');
      setTimeout(() => setAddToCartMessage(null), 3000);
    } finally {
      setAddingToCart(false);
    }
  };

  const getVariantLabel = (variant: ProductVariant) => {
    return variant.properties.map((p) => `${p.property.title}: ${p.value.title}`).join(', ');
  };

  const displayedPrice = (() => {
    if (selectedVariantId && product?.variants) {
      const selectedVariant = product.variants.find((v) => v.id === selectedVariantId);
      if (selectedVariant) return selectedVariant.price;
    }
    return product?.price || 0;
  })();

  const productBadges =
    product?.badges && product.badges.length > 0
      ? product.badges
      : product?.social_proof_badges || [];

  // Helper function to get badge color classes based on index
  const getBadgeColorClasses = (index: number) => {
    const colors = [
      // Blue
      {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-700 dark:text-blue-300',
        iconFilter: 'brightness(0) saturate(100%) invert(24%) sepia(89%) saturate(4823%) hue-rotate(220deg) brightness(91%) contrast(91%)',
      },
      // Purple
      {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-800',
        text: 'text-purple-700 dark:text-purple-300',
        iconFilter: 'brightness(0) saturate(100%) invert(14%) sepia(82%) saturate(6073%) hue-rotate(267deg) brightness(80%) contrast(94%)',
      },
      // Pink
      {
        bg: 'bg-pink-50 dark:bg-pink-900/20',
        border: 'border-pink-200 dark:border-pink-800',
        text: 'text-pink-700 dark:text-pink-300',
        iconFilter: 'brightness(0) saturate(100%) invert(12%) sepia(97%) saturate(5748%) hue-rotate(332deg) brightness(82%) contrast(95%)',
      },
      // Orange
      {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        border: 'border-orange-200 dark:border-orange-800',
        text: 'text-orange-700 dark:text-orange-300',
        iconFilter: 'brightness(0) saturate(100%) invert(20%) sepia(96%) saturate(3855%) hue-rotate(11deg) brightness(90%) contrast(96%)',
      },
      // Emerald
      {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        text: 'text-emerald-700 dark:text-emerald-300',
        iconFilter: 'brightness(0) saturate(100%) invert(29%) sepia(100%) saturate(1752%) hue-rotate(141deg) brightness(84%) contrast(98%)',
      },
      // Indigo
      {
        bg: 'bg-indigo-50 dark:bg-indigo-900/20',
        border: 'border-indigo-200 dark:border-indigo-800',
        text: 'text-indigo-700 dark:text-indigo-300',
        iconFilter: 'brightness(0) saturate(100%) invert(18%) sepia(83%) saturate(6031%) hue-rotate(237deg) brightness(71%) contrast(97%)',
      },
      // Teal
      {
        bg: 'bg-teal-50 dark:bg-teal-900/20',
        border: 'border-teal-200 dark:border-teal-800',
        text: 'text-teal-700 dark:text-teal-300',
        iconFilter: 'brightness(0) saturate(100%) invert(33%) sepia(89%) saturate(850%) hue-rotate(141deg) brightness(85%) contrast(93%)',
      },
      // Cyan
      {
        bg: 'bg-cyan-50 dark:bg-cyan-900/20',
        border: 'border-cyan-200 dark:border-cyan-800',
        text: 'text-cyan-700 dark:text-cyan-300',
        iconFilter: 'brightness(0) saturate(100%) invert(33%) sepia(82%) saturate(2057%) hue-rotate(166deg) brightness(85%) contrast(93%)',
      },
      // Rose
      {
        bg: 'bg-rose-50 dark:bg-rose-900/20',
        border: 'border-rose-200 dark:border-rose-800',
        text: 'text-rose-700 dark:text-rose-300',
        iconFilter: 'brightness(0) saturate(100%) invert(12%) sepia(94%) saturate(6139%) hue-rotate(344deg) brightness(80%) contrast(97%)',
      },
      // Amber
      {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        text: 'text-amber-700 dark:text-amber-300',
        iconFilter: 'brightness(0) saturate(100%) invert(28%) sepia(97%) saturate(2562%) hue-rotate(22deg) brightness(88%) contrast(95%)',
      },
    ];
    return colors[index % colors.length];
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          className={`w-3.5 h-3.5 ${i <= rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    return stars;
  };

  // Mini product card for similar/vendor products sections
  const MiniProductCard: React.FC<{ item: SimpleProduct }> = ({ item }) => (
    <div
      className="group w-[164px] shrink-0 cursor-pointer overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white transition-shadow hover:shadow-ds-2"
      onClick={() => {
        // Open product detail for this product - reload modal
        if (onNavigateToProduct) {
          onNavigateToProduct(item.id);
        } else {
          // Fallback: open in basalam
          openExternalUrl(`https://basalam.com/${item.vendor_identifier}/product/${item.id}`);
        }
      }}
    >
      <div className="aspect-square overflow-hidden bg-ds-subtle">
        <img
          src={item.photo}
          alt={item.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="space-y-1.5 p-2.5">
        <h5 className="line-clamp-2-safe text-right text-xs font-semibold leading-5 text-ds-ink">
          {item.title}
        </h5>
        <p className="truncate text-right text-[10px] text-ds-500">
          غرفه: {item.vendor_name}
        </p>
        <div className="flex items-center justify-end gap-1 pt-1">
          <span className="text-sm font-bold text-ds-ink">
            {formatPrice(item.price)}
          </span>
          <svg className="h-4 w-4 text-ds-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center app-sm:items-center app-sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={requestClose} />

      {/* Previous Button */}
      {previousProductId && onNavigateToProduct && (
        <button
          onClick={() => onNavigateToProduct(previousProductId)}
          className="absolute left-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-ds-100 bg-ds-white/90 text-ds-700 shadow-ds-3 hover:bg-ds-white app-sm:flex"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next Button */}
      {nextProductId && onNavigateToProduct && (
        <button
          onClick={() => onNavigateToProduct(nextProductId)}
          className="absolute right-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-ds-100 bg-ds-white/90 text-ds-700 shadow-ds-3 hover:bg-ds-white app-sm:flex"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Modal */}
      <div className="relative flex max-h-[calc(100%_-_16px)] w-full max-w-[820px] flex-col overflow-hidden rounded-t-ds-16 bg-ds-white shadow-ds-3 app-sm:max-h-[90vh] app-sm:rounded-ds-16">
        {/* Header */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-ds-100 bg-ds-white px-4">
          <button
            onClick={requestClose}
            className="grid h-9 w-9 place-items-center rounded-ds-6 text-ds-600 hover:bg-ds-subtle"
            aria-label="بستن جزئیات محصول"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-[15px] font-bold text-ds-ink">جزئیات محصول</h2>
        </div>

        {/* Content */}
        <div className="ds-scrollbar min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-ds-primary border-t-transparent"></div>
            </div>
          ) : product ? (
            <div dir="rtl" className="app-sm:grid app-sm:grid-cols-[300px_minmax(0,1fr)]">
              {/* Image Gallery */}
              <div className="self-start bg-ds-subtle app-sm:sticky app-sm:top-0">
                <div className="relative aspect-square max-h-80 mx-auto overflow-hidden">
                  <img
                    src={displayPhotos[selectedPhotoIndex] || product.photo}
                    alt={product.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                {displayPhotos.length > 1 && (
                  <div className="flex gap-2 px-4 pb-3 pt-2 overflow-x-auto">
                    {displayPhotos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedPhotoIndex(index)}
                        className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedPhotoIndex === index
                            ? 'border-ds-primary shadow-ds-1'
                            : 'border-ds-200 opacity-70'
                        }`}
                      >
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-5 px-4 py-4">
                {/* Product Title */}
                <h3 className="text-right text-base font-bold leading-7 text-ds-ink">
                  {product.title}
                </h3>

                {/* Vendor Info */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs text-ds-700">
                    <img src="https://statics.basalam.com/public-174/admin/lVQnz3/03-03/4bUd8uix125YksZNEJTnYzDtcm3axe7962kuCMpw0fEbcDwKHu.SVG" alt="" className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium text-right">غرفه {product.vendor.name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-ds-500">
                    <img src="https://statics.basalam.com/public-174/admin/lVQnz3/03-03/4tEQib8PJl4nMINpsVF0tTs8zEfRlilRXw7Ib2z6aMwGf9YVDX.SVG" alt="" className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="text-right">
                      {product.province && `${product.province}، `}{product.city}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center justify-end gap-2 flex-row-reverse">
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-right">
                    ({reviewsTotalCount || product.reviews} نظر)
                  </span>
                  <div className="flex items-center gap-1 flex-row-reverse">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 text-right">
                      {product.rating.toFixed(1)}
                    </span>
                    <div className="flex flex-row-reverse">{renderStars(Math.round(product.rating))}</div>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center gap-2 ">
                  <p className="text-[10px] text-ds-500">قیمت نهایی</p>
                  <p className="text-xl font-bold text-ds-ink">{formatPrice(displayedPrice)}</p>
                </div>

                {/* Badges */}
                {productBadges.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {(showAllBadges ? productBadges : productBadges.slice(0, 2)).map((badge, index) => {
                      const colorClasses = getBadgeColorClasses(index);
                      return (
                        <div
                          key={`${badge.type}-${badge.id}`}
                          className={`flex items-center gap-2 w-full px-3 py-2.5 ${colorClasses.bg} rounded-b-lg border ${colorClasses.border}`}
                        >
                          {(badge.icon || badge.badge) && (
                            <img
                              src={badge.icon || badge.badge}
                              alt=""
                              className="w-5 h-5 flex-shrink-0 object-contain"
                              style={{
                                filter: colorClasses.iconFilter,
                              }}
                            />
                          )}
                          <span className={`text-xs font-semibold ${colorClasses.text}`}>
                            {badge.title}
                          </span>
                        </div>
                      );
                    })}
                    {!showAllBadges && productBadges.length > 2 && (
                      <button
                        onClick={() => setShowAllBadges(true)}
                        className="w-full rounded-ds-6 border border-ds-200 px-3 py-2.5 text-xs font-semibold text-ds-600 transition-colors hover:border-ds-primary hover:text-ds-primary-strong"
                      >
                        نمایش {productBadges.length - 2} نمایش بیشتر
                      </button>
                    )}
                  </div>
                )}

                {/* Variants */}
                {product.variants.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">تنوع رو انتخاب کن:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.variants.map((variant) => (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariantId(variant.id)}
                          disabled={variant.stock === 0}
                          className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                            selectedVariantId === variant.id
                              ? 'border-ds-primary bg-ds-primary-soft text-ds-primary-strong'
                              : 'border-ds-200 bg-ds-white text-ds-700 hover:border-ds-primary'
                          } ${variant.stock === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {getVariantLabel(variant)}
                          {variant.stock === 0 && ' (ناموجود)'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vendor Products Section */}
                {vendorProducts.length > 0 && (
                  <div className="space-y-3 pt-2">
                    <div className="flex justify-start">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white">بقیه محصولات این غرفه</h4>
                    </div>
                    <div 
                      ref={vendorProductsContainerRef}
                      className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4"
                    >
                      {vendorProducts.map((item) => (
                        <MiniProductCard key={item.id} item={item} />
                      ))}
                      {hasMoreVendorProducts && (
                        <div className="flex-shrink-0 w-40 h-40 flex items-center justify-center">
                          {loadingVendorProducts && (
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-ds-primary border-t-transparent"></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Similar Products Section */}
                {similarProducts.length > 0 && (
                  <div className="space-y-3 pt-2">
                    <div className="flex justify-start">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white">محصولات مشابه</h4>
                    </div>
                    <div 
                      ref={similarProductsContainerRef}
                      className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4"
                    >
                      {similarProducts.map((item) => (
                        <MiniProductCard key={item.id} item={item} />
                      ))}
                      {hasMoreSimilarProducts && (
                        <div className="flex-shrink-0 w-40 h-40 flex items-center justify-center">
                          {loadingSimilarProducts && (
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-ds-primary border-t-transparent"></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reviews / Shopping Experiences */}
                {reviews.length > 0 && (
                  <div className="space-y-3 pt-2">
                    <div className="flex justify-start">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                        تجربه‌های خرید
                      </h4>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
                      {reviews.map((review) => (
                        <div
                          key={review.id}
                          className="flex-shrink-0 w-64 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700"
                          dir="rtl"
                        >
                          <div className="flex items-center justify-between mb-2 flex-row-reverse">
                            <div className="flex items-center gap-2 flex-row-reverse">
                              {review.user.photo ? (
                                <img
                                  src={review.user.photo}
                                  alt={review.user.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                    {review.user.name.charAt(0)}
                                  </span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-gray-900 dark:text-white text-sm block text-right">
                                  {review.user.name}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-row-reverse">{renderStars(review.star)}</div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed line-clamp-4 text-right">
                            {review.description}
                          </p>
                          {review.photos.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {review.photos.map((photo, idx) => (
                                <img
                                  key={idx}
                                  src={photo}
                                  alt=""
                                  className="w-14 h-14 rounded-lg object-cover"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {hasMoreReviews && (
                        <div className="flex-shrink-0 w-64 flex items-center justify-center">
                          {loadingReviews ? (
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-ds-primary border-t-transparent"></div>
                          ) : (
                            <button
                              onClick={handleLoadMoreReviews}
                              className="rounded-ds-6 border border-ds-200 px-4 py-2 text-xs font-medium text-ds-600 transition-colors hover:border-ds-primary hover:text-ds-primary-strong"
                            >
                              نظرهای بیشتر رو ببین
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              بارگذاری اطلاعات محصول با خطا روبرو شد
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {product && !loading && (
          <div className="space-y-2 border-t border-ds-100 bg-ds-white px-4 py-3">
            {addToCartMessage && (
              <p
                className={`text-xs text-center ${
                  addToCartMessage.includes('✓')
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {addToCartMessage}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || (product.variants.length > 0 && !selectedVariantId)}
                className={`flex-1 flex items-center justify-center gap-2  py-3 rounded-xl font-medium text-sm transition-all ${
                  addingToCart || (product.variants.length > 0 && !selectedVariantId)
                    ? 'cursor-not-allowed bg-ds-200 text-ds-500'
                    : 'bg-ds-primary text-white hover:bg-ds-primary-strong'
                }`}
              >
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="white" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                    </svg>
                {addingToCart ? 'در حال افزودن...' : 'اضافه کردن به سبد خرید'}
              </button>
              <button
                onClick={() => {
                  if (agent_chat_id) {
                    void eventService.captureProductClick(agent_chat_id, productId, 'pdp');
                  }
                  openExternalUrl(product.link);
                }}
                className="rounded-ds-6 border border-ds-200 bg-ds-white px-4 py-3 text-sm font-medium text-ds-700 hover:bg-ds-subtle"
              >
                مشاهده در باسلام
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
